import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Breadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  /** Zone d'actions à droite (boutons, etc.). */
  actions?: ReactNode;
  className?: string;
}

/**
 * En-tête de page : breadcrumb, titre, sous-titre optionnel, zone d'actions.
 * Brief — composant #4.
 */
export function PageHeader({ title, subtitle, breadcrumbs, actions, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-6 flex flex-col gap-3', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Fil d'Ariane" className="flex items-center gap-1 text-xs text-content-muted">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${i}`}>
                {crumb.to && !isLast ? (
                  <Link to={crumb.to} className="font-medium hover:text-brand-primary">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={cn(isLast && 'font-semibold text-content-secondary')}>{crumb.label}</span>
                )}
                {!isLast && <ChevronRight className="size-3.5" />}
              </Fragment>
            );
          })}
        </nav>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight text-content-primary">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-content-secondary">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
